// libs/optimizarImagen.js
//
// Recomprime a WebP en el navegador antes de subir la imagen de un taller.
//
// Por qué acá y no en el backend: el egress de S3 en sa-east-1 es lo que domina
// el costo del bucket (~$0,13/GB) y los PNG que subía el admin llegaban a 4,5 MB,
// servidos tal cual al navegador de cada visitante. Convertirlos deja la imagen
// en ~100 KB. La Lambda no puede usar sharp con el empaquetado actual (esbuild
// inlinea el JS pero el binario nativo no viaja en el zip), y hacerlo acá además
// hace la subida ~40 veces más liviana.
//
// Ante cualquier problema devuelve el archivo original: nunca debe impedir subir.

const MAX_ANCHO = 1600;   // las tarjetas pintan 176 px de alto; el detalle, ancho completo
const CALIDAD = 0.78;

let soportaWebpCache = null;

// ¿El canvas de este navegador sabe codificar WebP? (todos los modernos, sí)
const soportaWebp = () => {
  if (soportaWebpCache !== null) return soportaWebpCache;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    soportaWebpCache = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    soportaWebpCache = false;
  }
  return soportaWebpCache;
};

// createImageBitmap respeta la orientación EXIF de las fotos de celular; si no
// está disponible, se cae a un <img> tradicional.
const cargarImagen = async (file) => {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // sigue al fallback
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('No se pudo leer la imagen'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};

const nombreWebp = (nombre = 'imagen') => `${nombre.replace(/\.[^.]+$/, '')}.webp`;

export const convertirImagenAWebp = async (file) => {
  if (!file || !file.type?.startsWith('image/')) return file;
  if (file.type === 'image/gif') return file;      // el canvas perdería la animación
  if (file.type === 'image/webp' && file.size <= 300 * 1024) return file;  // ya está liviana
  if (!soportaWebp()) return file;

  try {
    const imagen = await cargarImagen(file);
    const anchoOriginal = imagen.width;
    const altoOriginal = imagen.height;
    if (!anchoOriginal || !altoOriginal) return file;

    const escala = Math.min(1, MAX_ANCHO / anchoOriginal);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(anchoOriginal * escala);
    canvas.height = Math.round(altoOriginal * escala);

    const ctx = canvas.getContext('2d');
    ctx.drawImage(imagen, 0, 0, canvas.width, canvas.height);
    imagen.close?.();

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', CALIDAD));
    if (!blob || blob.size >= file.size) return file;   // si no mejora, se sube el original

    console.log(
      `Imagen optimizada: ${(file.size / 1024).toFixed(0)} KB (${anchoOriginal}x${altoOriginal}) → ` +
      `${(blob.size / 1024).toFixed(0)} KB (${canvas.width}x${canvas.height} webp)`
    );

    return new File([blob], nombreWebp(file.name), { type: 'image/webp', lastModified: Date.now() });
  } catch (error) {
    console.warn('No se pudo optimizar la imagen, se sube el original:', error);
    return file;
  }
};
